// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import dayjs from 'dayjs';

import { useUser, useRooftop, useCustomerConfig } from '@store';

import { ReportingRange, ReportingTab, ReportingConfig, ReportingModuleGroup, AppointmentManagementConfig } from '../types';
import {
  DEFAULT_REPORTING_RANGE,
  getRangeEnd,
  getRangeStart,
  isValidReportingRange,
} from '../utils/reportingDates';
import { isAdminStyleRole, parseJsonIfNeeded } from '../utils/reportingGuards';
import { isModuleVisible as isModuleVisibleUtil, isTabVisible } from '../utils/reportingVisibility';

// TYPES ---------------------------------------------------------------- //

interface ReportingContextType {
  // Context
  customerId: number;

  // Filter state
  range: ReportingRange;
  start: string;
  end: string;
  disablePickers: boolean;

  // Tab state
  selectedTab: ReportingTab;

  // Availability
  reportingEnabled: boolean;
  appointmentManagementEnabled: boolean;
  executiveKpisEnabled: boolean;
  appointmentConfig?: AppointmentManagementConfig;

  // Module-level visibility (driven by the `reporting` config)
  isModuleVisible: (tab: ReportingTab, group: ReportingModuleGroup, moduleKey: string) => boolean;

  // Handlers
  setSelectedTab: (tab: ReportingTab) => void;
  onRangeChange: (range: ReportingRange) => void;
  onDateChange: (type: 'start' | 'end') => (date: string) => void;
}

interface ReportingProviderProps {
  children: React.ReactNode;
}

// GETTERS ---------------------------------------------------------------- //

const getStoredRange = (): ReportingRange => {
  try {
    const stored = localStorage.getItem('reporting-dashboard-range');
    return isValidReportingRange(stored) ? stored : DEFAULT_REPORTING_RANGE;
  } catch (err) {
    console.error('Error getting reporting range', err);
    return DEFAULT_REPORTING_RANGE;
  }
};

const getStoredStart = (range: ReportingRange): string => {
  try {
    const stored = localStorage.getItem('reporting-dashboard-start');
    return getRangeStart(range, stored);
  } catch (err) {
    console.error('Error getting reporting start date', err);
    return getRangeStart(DEFAULT_REPORTING_RANGE);
  }
};

const getStoredEnd = (range: ReportingRange): string => {
  try {
    const stored = localStorage.getItem('reporting-dashboard-end');
    return getRangeEnd(range, stored);
  } catch (err) {
    console.error('Error getting reporting end date', err);
    return getRangeEnd(DEFAULT_REPORTING_RANGE);
  }
};

const getStoredTab = (): ReportingTab => {
  try {
    const stored = localStorage.getItem('reporting-dashboard-tab');
    if (stored === 'operations' || stored === 'appointments' || stored === 'executive') return stored;
    return 'operations';
  } catch (err) {
    console.error('Error getting reporting tab', err);
    return 'operations';
  }
};

// CONTEXT ---------------------------------------------------------------- //

const ReportingContext = React.createContext<ReportingContextType | undefined>(undefined);

export const ReportingProvider = ({ children }: ReportingProviderProps) => {
  const user = useUser();
  const rooftop = useRooftop();
  const customerConfig = useCustomerConfig();

  // Derive customer ID from selected rooftop, falling back to the user profile
  const customerId: number = rooftop?.id || user?.profile?.customer_id || 0;

  // Filter state
  const [range, setRange] = React.useState<ReportingRange>(getStoredRange());
  const [start, setStart] = React.useState<string>(getStoredStart(range));
  const [end, setEnd] = React.useState<string>(getStoredEnd(range));
  const [selectedTabState, setSelectedTabState] = React.useState<ReportingTab>(getStoredTab());

  const disablePickers = range !== 'custom';

  // Availability
  const reporting = customerConfig?.reporting as ReportingConfig | undefined;
  const reportingEnabled = reporting?.enabled === true;

  const appointmentConfig = React.useMemo<AppointmentManagementConfig | undefined>(() => {
    const config = parseJsonIfNeeded<{ appointment_management?: AppointmentManagementConfig }>(rooftop?.config);
    return config?.appointment_management;
  }, [rooftop?.config]);

  // Tabs require both their underlying feature (appointment management / admin role)
  // AND the per-tab visibility flag from the reporting config.
  const appointmentManagementEnabled = appointmentConfig?.enabled === true && isTabVisible(reporting, 'appointments');
  const executiveKpisEnabled = isAdminStyleRole(user?.profile?.default_role) && isTabVisible(reporting, 'executive');

  const isModuleVisible = React.useCallback(
    (tab: ReportingTab, group: ReportingModuleGroup, moduleKey: string) =>
      isModuleVisibleUtil(reporting, tab, group, moduleKey),
    [reporting]
  );

  // Coerce the selected tab to one that is currently available. During the
  // initial async rooftop load, gated tabs are briefly unavailable, so the UI
  // should fall back to operations without losing the persisted selection.
  const selectedTab: ReportingTab =
    (selectedTabState === 'appointments' && !appointmentManagementEnabled) ||
    (selectedTabState === 'executive' && !executiveKpisEnabled)
      ? 'operations'
      : selectedTabState;

  // If the persisted/selected tab becomes unavailable (e.g. after a rooftop
  // change), fall back to the operations tab. Wait until the rooftop has
  // loaded so a persisted tab is not stomped during the initial async load.
  React.useEffect(() => {
    if (!rooftop) return;
    if (
      (selectedTabState === 'appointments' && !appointmentManagementEnabled) ||
      (selectedTabState === 'executive' && !executiveKpisEnabled)
    ) {
      setSelectedTabState('operations');
    }
  }, [rooftop, selectedTabState, appointmentManagementEnabled, executiveKpisEnabled]);

  // HANDLERS //

  /** Handle changing the selected tab */
  const handleSetSelectedTab = (tab: ReportingTab) => {
    try {
      localStorage.setItem('reporting-dashboard-tab', tab);
    } catch (err) {
      console.error('Error storing reporting tab', err);
    }
    setSelectedTabState(tab);
  };

  /** Handle changing the date range preset */
  const handleRangeChange = (newRange: ReportingRange) => {
    try {
      localStorage.setItem('reporting-dashboard-range', newRange);
      setRange(newRange);
      setStart(getStoredStart(newRange));
      setEnd(getStoredEnd(newRange));
    } catch (err) {
      console.error('Error changing reporting range', err);
    }
  };

  /** Handle changing a custom date */
  const handleDateChange = (type: 'start' | 'end') => (date: string) => {
    if (!date) return;

    if (type === 'start') {
      const newStart = dayjs(date).startOf('day').format();
      if (new Date(newStart).getTime() <= 0) return;
      try {
        localStorage.setItem('reporting-dashboard-start', newStart);
      } catch (err) {
        console.error('Error storing reporting start date', err);
      }
      setStart(newStart);
    } else {
      const newEnd = dayjs(date).endOf('day').format();
      if (new Date(newEnd).getTime() <= 0) return;
      try {
        localStorage.setItem('reporting-dashboard-end', newEnd);
      } catch (err) {
        console.error('Error storing reporting end date', err);
      }
      setEnd(newEnd);
    }
  };

  const contextValue: ReportingContextType = {
    customerId,

    range,
    start,
    end,
    disablePickers,

    selectedTab,

    reportingEnabled,
    appointmentManagementEnabled,
    executiveKpisEnabled,
    appointmentConfig,

    isModuleVisible,

    setSelectedTab: handleSetSelectedTab,
    onRangeChange: handleRangeChange,
    onDateChange: handleDateChange,
  };

  return <ReportingContext.Provider value={contextValue}>{children}</ReportingContext.Provider>;
};

// HOOK ---------------------------------------------------------------- //

export const useReporting = (): ReportingContextType => {
  const context = React.useContext(ReportingContext);
  if (context === undefined) {
    throw new Error('useReporting must be used within a ReportingProvider');
  }
  return context;
};
